/**
 * Icon — render @hugeicons/core-free-icons data as inline SVG.
 *
 * Each icon export is an array of [tag, attrs] tuples (lucide-shaped).
 * We build a real SVG element so it inherits `color` via stroke=currentColor
 * and scales with `width/height` CSS.
 */

import Menu01Icon from "@hugeicons/core-free-icons/Menu01Icon";
import Search01Icon from "@hugeicons/core-free-icons/Search01Icon";
import MoreVerticalIcon from "@hugeicons/core-free-icons/MoreVerticalIcon";
import AttachmentIcon from "@hugeicons/core-free-icons/AttachmentIcon";
import Mic01Icon from "@hugeicons/core-free-icons/Mic01Icon";
import PlusSignIcon from "@hugeicons/core-free-icons/PlusSignIcon";
import PinIcon from "@hugeicons/core-free-icons/PinIcon";
import VolumeMute01Icon from "@hugeicons/core-free-icons/VolumeMute01Icon";
import MessageAdd01Icon from "@hugeicons/core-free-icons/MessageAdd01Icon";
import Robot01Icon from "@hugeicons/core-free-icons/Robot01Icon";
import StethoscopeIcon from "@hugeicons/core-free-icons/StethoscopeIcon";
import ToolsIcon from "@hugeicons/core-free-icons/ToolsIcon";
import NoteEditIcon from "@hugeicons/core-free-icons/NoteEditIcon";
import BotIcon from "@hugeicons/core-free-icons/BotIcon";
import Activity03Icon from "@hugeicons/core-free-icons/Activity03Icon";
import CheckmarkCircle02Icon from "@hugeicons/core-free-icons/CheckmarkCircle02Icon";
import Clock01Icon from "@hugeicons/core-free-icons/Clock01Icon";
import Cancel01Icon from "@hugeicons/core-free-icons/Cancel01Icon";
import ArrowUp01Icon from "@hugeicons/core-free-icons/ArrowUp01Icon";
import Settings01Icon from "@hugeicons/core-free-icons/Settings01Icon";
import Logout03Icon from "@hugeicons/core-free-icons/Logout03Icon";
import TranslateIcon from "@hugeicons/core-free-icons/TranslateIcon";
import ArrowRight01Icon from "@hugeicons/core-free-icons/ArrowRight01Icon";
import ArrowDown01Icon from "@hugeicons/core-free-icons/ArrowDown01Icon";
import Camera01Icon from "@hugeicons/core-free-icons/Camera01Icon";
import LinkSquare02Icon from "@hugeicons/core-free-icons/LinkSquare02Icon";

type IconNode = [string, Record<string, string>];
type IconData = IconNode[];

const REGISTRY: Record<string, IconData> = {
  menu: Menu01Icon as IconData,
  search: Search01Icon as IconData,
  more: MoreVerticalIcon as IconData,
  attach: AttachmentIcon as IconData,
  mic: Mic01Icon as IconData,
  plus: PlusSignIcon as IconData,
  pin: PinIcon as IconData,
  mute: VolumeMute01Icon as IconData,
  newChat: MessageAdd01Icon as IconData,
  // Agent avatar icons
  robot: Robot01Icon as IconData,
  stethoscope: StethoscopeIcon as IconData,
  tools: ToolsIcon as IconData,
  noteEdit: NoteEditIcon as IconData,
  bot: BotIcon as IconData,
  // Status / panel icons
  activity: Activity03Icon as IconData,
  check: CheckmarkCircle02Icon as IconData,
  clock: Clock01Icon as IconData,
  close: Cancel01Icon as IconData,
  send: ArrowUp01Icon as IconData,
  // Settings drawer
  settings: Settings01Icon as IconData,
  logout: Logout03Icon as IconData,
  translate: TranslateIcon as IconData,
  chevronRight: ArrowRight01Icon as IconData,
  arrowDown: ArrowDown01Icon as IconData,
  // Pair wall
  camera: Camera01Icon as IconData,
  atmos: LinkSquare02Icon as IconData,
};

const SVG_NS = "http://www.w3.org/2000/svg";

export type IconName = keyof typeof REGISTRY;

export function Icon(name: IconName, sizePx = 20): SVGSVGElement {
  const data = REGISTRY[name];
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("width", String(sizePx));
  svg.setAttribute("height", String(sizePx));
  svg.setAttribute("fill", "none");
  svg.setAttribute("aria-hidden", "true");
  svg.classList.add("icon");

  for (const [tag, attrs] of data) {
    const node = document.createElementNS(SVG_NS, tag);
    for (const [k, v] of Object.entries(attrs)) {
      if (k === "key") continue;
      // Hugeicons uses camelCase (strokeWidth). SVG attribute names need
      // kebab-case (stroke-width). Convert.
      const attrName = k.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
      node.setAttribute(attrName, v);
    }
    svg.appendChild(node);
  }
  return svg;
}

export default Icon;
